<template>
  <div class="fee-detail-footer">
    <div class="fee-detail-footer--left">
      <p class="opacity-item borrow-paid">{{ $t("estimate.borrowerPaid") }}</p>
      <p>
        <span
          class="left-dollar"
          v-if="footerData.cashToCloseBreakdown.borrowerPaid >= 0"
          >$</span
        >
        <span class="left-amount">{{
          footerData.cashToCloseBreakdown.borrowerPaid | currencyNonDollar(true)
        }}</span>
      </p>
    </div>

    <div class="fee-detail-footer--center">
      <div class="center-item">
        <p class="opacity-item">{{ $t("estimate.points") }}</p>
        <p class="detail-amount">
          {{ footerData.cashToCloseBreakdown.points | currencyAbsolute }}
        </p>
      </div>
      <div class="center-item">
        <p class="opacity-item">{{ $t("estimate.prepaidInterest") }}</p>
        <p class="detail-amount">
          {{
            footerData.cashToCloseBreakdown.prepaidInterest | currencyAbsolute
          }}
        </p>
      </div>
      <div class="center-item">
        <p class="opacity-item">
          <span>{{
            footerData.cashToCloseBreakdown.ufmipFfGfSpLabel || "UFMIP/FF/GF/SP"
          }}</span>
          <info-tooltip :info="ufmipFfGfSp" />
        </p>
        <p class="detail-amount">
          {{ footerData.cashToCloseBreakdown.ufmipFfGfSp | currencyAbsolute }}
        </p>
      </div>
    </div>

    <div class="fee-detail-footer--right">
      <div class="right-item">
        <p class="opacity-item">{{ $t("estimate.lenderPaid") }}</p>
        <p class="detail-amount">
          {{ footerData.cashToCloseBreakdown.lenderPaid | currencyAbsolute }}
        </p>
      </div>
      <div
        v-if="getAssets?.loanPurposeType !== refinanceLoanType"
        class="right-item"
      >
        <p class="opacity-item">{{ $t("estimate.sellerPaid") }}</p>
        <p class="detail-amount">
          {{ footerData.cashToCloseBreakdown.sellerPaid | currencyAbsolute }}
        </p>
      </div>
      <div class="right-item">
        <p class="opacity-item">
          {{
            getAssets?.loanPurposeType === refinanceLoanType
              ? $t("estimate.lenderCredit")
              : $t("estimate.earnestMoney")
          }}
        </p>
        <p class="detail-amount">
          {{ footerData.cashToCloseBreakdown.earnestMoney | currencyAbsolute }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import InfoTooltip from "../Icons//InfoToolTip.vue";
import { REFINANCE_LOAN_TYPE } from "./../../constants";

// The tooltip is rendered as HTML, and its text now comes back from a
// translator - so every translated piece is escaped before the markup is built.
const escapeHtml = (text) =>
  String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

// [term, explanation, optional aside after the term], in display order.
const UPFRONT_FEE_TERMS = [
  ["estimate.ufmipTerm", "estimate.ufmipDesc"],
  ["estimate.ffTerm", "estimate.ffDesc"],
  ["estimate.gfTerm", "estimate.gfDesc", "estimate.gfAside"],
  ["estimate.spTerm", "estimate.spDesc"],
];

export default {
  name: "FeeDetailFooter",
  components: {
    InfoTooltip,
  },
  props: {
    footerData: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      refinanceLoanType: REFINANCE_LOAN_TYPE,
    };
  },
  computed: {
    ...mapGetters(["getAssets"]),
    // Built per language from the catalog rather than held as one HTML string,
    // so the markup never passes through the translator.
    ufmipFfGfSp() {
      return UPFRONT_FEE_TERMS.map(([term, desc, aside]) => {
        const note = aside ? ` ${escapeHtml(this.$t(aside))}` : "";
        return `<strong>${escapeHtml(this.$t(term))}</strong>${note} - ${escapeHtml(
          this.$t(desc)
        )}`;
      }).join(" <br /> <br /> ");
    },
  },
};
</script>

<style lang="scss" scoped>
.fee-detail-footer {
  background: #15162b;
  border-radius: 0 0 9px 9px;
  display: flex;
  justify-content: space-between;
  padding: 24px 34px 12px 40px;
  gap: 154px;
  min-width: max-content;
  width: 100%;

  .borrow-paid {
    margin-bottom: 24px;
  }

  p {
    color: #ffffff;
  }

  .opacity-item {
    opacity: 0.7;
  }

  .detail-amount {
    font-weight: 500;
  }

  .center-item,
  .right-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 64px;
    margin-bottom: 8px;
  }
  &--center {
    min-width: 254px;
  }
  &--right {
    min-width: 216px;
  }
  &--left {
    min-width: 100px;
    .left-amount {
      font-weight: 500;
      font-size: 35px;
      line-height: 52.5px;
      letter-spacing: -0.5px;
      opacity: 1;
    }

    .left-dollar {
      font-weight: 500;
      font-size: 22px;
      line-height: 33px;
      letter-spacing: -0.5px;
    }
  }
}

@media all and (max-width: 1079px) {
  .fee-dialog:not(.layout-pdf) .fee-detail-footer {
    gap: unset;
  }
}

@media all and (max-width: 899px) {
  .fee-dialog:not(.layout-pdf) .fee-detail-footer {
    flex-direction: column;
    padding: 24px 40px;

    &--left {
      padding-bottom: 24px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.2);
    }
    &--center {
      padding-top: 28px;
    }

    .center-item,
    .right-item {
      gap: unset;
    }
  }
}

@media all and (max-width: 600px) {
  .fee-dialog:not(.layout-pdf) .fee-detail-footer {
    padding: 24px 14px;
    .borrow-paid {
      margin-bottom: 4px;
    }
  }
}
</style>
